import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import { STANDARD_CTA_BUTTON_COMPACT_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { useNotification } from '../../providers/NotificationProvider';
import { useConfirm } from '../../providers/ConfirmProvider';
import { useAuth } from '../../firebase/AuthProvider';
import type { MarketAssetType, MarketCurrency, MarketMarket, MarketSymbol, MarketSymbolInput } from '../../hooks/useMarketSymbols';

/**
 * 銘柄管理ダイアログ。登録済み銘柄の一覧 (蓄積状況付き) と、追加・有効切替・削除。
 * 追加時はバックエンドが 5 年分をバックフィルするため、完了まで数秒かかる。
 */

const ASSET_LABEL: Record<MarketAssetType, string> = { fx: '為替', stock: '個別株', index: '指数' };
const MARKET_LABEL: Record<MarketMarket, string> = { FX: 'FX', JP: '東証', US: '米国' };

type FormState = {
  symbol_code: string;
  display_name: string;
  asset_type: MarketAssetType;
  market: MarketMarket;
  currency: MarketCurrency;
  sort_no: string;
};

const emptyForm: FormState = { symbol_code: '', display_name: '', asset_type: 'stock', market: 'JP', currency: 'JPY', sort_no: '100' };

type Props = {
  open: boolean;
  symbols: MarketSymbol[];
  onClose: () => void;
  onSave: (input: MarketSymbolInput) => Promise<{ symbolCode: string; isNew: boolean; backfilled: number }>;
  onRemove: (symbolCode: string) => Promise<void>;
};

export default function MarketSymbolDialog({ open, symbols, onClose, onSave, onRemove }: Props) {
  const notify = useNotification();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // 市場に応じて通貨の既定値を追従させる (FX / 東証 は JPY、米国は USD)
  const setMarket = (m: MarketMarket) =>
    setForm((prev) => ({ ...prev, market: m, currency: m === 'US' ? 'USD' : 'JPY', asset_type: m === 'FX' ? 'fx' : prev.asset_type === 'fx' ? 'stock' : prev.asset_type }));

  const handleAdd = async () => {
    setSaving(true);
    try {
      const r = await onSave({
        symbol_code: form.symbol_code.trim(),
        display_name: form.display_name.trim(),
        asset_type: form.asset_type,
        market: form.market,
        currency: form.currency,
        is_active: true,
        sort_no: Number(form.sort_no) || 100,
        operator_email: user?.email || undefined,
      });
      notify.success(r.isNew ? `${r.symbolCode} を登録し ${r.backfilled.toLocaleString()} 日分を取り込みました` : `${r.symbolCode} を更新しました`);
      setForm(emptyForm);
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (s: MarketSymbol, active: boolean) => {
    setBusyCode(s.symbol_code);
    try {
      await onSave({
        symbol_code: s.symbol_code, display_name: s.display_name, asset_type: s.asset_type, market: s.market,
        currency: s.currency, is_active: active, sort_no: s.sort_no, operator_email: user?.email || undefined,
      });
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '更新に失敗しました');
    } finally {
      setBusyCode(null);
    }
  };

  const handleRemove = async (s: MarketSymbol) => {
    const ok = await confirm({
      title: '銘柄の削除',
      message: `${s.display_name} (${s.symbol_code}) と蓄積済みの日足 ${s.coverage?.rows_total?.toLocaleString() ?? 0} 件を削除しますか？`,
      confirmText: '削除する',
      destructive: true,
    });
    if (!ok) return;
    setBusyCode(s.symbol_code);
    try {
      await onRemove(s.symbol_code);
      notify.success(`${s.symbol_code} を削除しました`);
    } catch (e: any) {
      notify.error(e?.response?.data?.error || e?.message || '削除に失敗しました');
    } finally {
      setBusyCode(null);
    }
  };

  const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px' } };
  const cellSx = { fontSize: '0.75rem', py: 0.5 };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, color: blueGrey[600], borderBottom: '1px solid #f1f5f9' }}>
        <Icons.CandlestickChart sx={{ fontSize: '18px' }} />
        銘柄管理
        <Chip size="small" label={`${symbols.length} 銘柄`} sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {/* 追加フォーム */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1.6fr 1fr 1fr 1fr 0.7fr auto', gap: 1, alignItems: 'center', mb: 2 }}>
          <TextField label="ティッカー" size="small" required value={form.symbol_code} onChange={set('symbol_code')} placeholder="7203.T / AAPL / JPY=X" sx={fieldSx} />
          <TextField label="表示名" size="small" value={form.display_name} onChange={set('display_name')} placeholder="未入力ならティッカー" sx={fieldSx} />
          <TextField label="市場" size="small" select value={form.market} onChange={(e) => setMarket(e.target.value as MarketMarket)} sx={fieldSx}>
            {(Object.keys(MARKET_LABEL) as MarketMarket[]).map((m) => <MenuItem key={m} value={m} sx={{ fontSize: '13px' }}>{MARKET_LABEL[m]}</MenuItem>)}
          </TextField>
          <TextField label="種別" size="small" select value={form.asset_type} onChange={set('asset_type')} sx={fieldSx}>
            {(Object.keys(ASSET_LABEL) as MarketAssetType[]).map((a) => <MenuItem key={a} value={a} sx={{ fontSize: '13px' }}>{ASSET_LABEL[a]}</MenuItem>)}
          </TextField>
          <TextField label="通貨" size="small" select value={form.currency} onChange={set('currency')} sx={fieldSx}>
            {(['JPY', 'USD'] as MarketCurrency[]).map((c) => <MenuItem key={c} value={c} sx={{ fontSize: '13px' }}>{c}</MenuItem>)}
          </TextField>
          <TextField label="表示順" size="small" type="number" value={form.sort_no} onChange={set('sort_no')} sx={fieldSx} />
          <Button
            onClick={handleAdd}
            disabled={saving || form.symbol_code.trim() === ''}
            startIcon={<Icons.Add />}
            sx={STANDARD_CTA_BUTTON_COMPACT_SX}
          >
            {saving ? '取込中...' : '追加'}
          </Button>
        </Box>
        <Typography sx={{ fontSize: '11px', color: '#64748b', mb: 1.5 }}>
          ティッカーは Yahoo Finance 表記 (東証は 4 桁コード + .T、指数は ^N225 / ^GSPC、為替は JPY=X)。追加時に過去 5 年分の日足を取り込みます。
        </Typography>

        {/* 一覧 */}
        <Table size="small">
          <TableHead>
            <TableRow>
              {['ティッカー', '表示名', '市場', '種別', '蓄積期間', '件数', '最終取込', '有効', ''].map((h) => (
                <TableCell key={h} sx={{ ...cellSx, fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {symbols.map((s) => (
              <TableRow key={s.symbol_code} hover sx={{ opacity: s.is_active ? 1 : 0.55 }}>
                <TableCell sx={{ ...cellSx, fontFamily: 'monospace', fontWeight: 700 }}>{s.symbol_code}</TableCell>
                <TableCell sx={cellSx}>{s.display_name}</TableCell>
                <TableCell sx={cellSx}>{MARKET_LABEL[s.market] ?? s.market}</TableCell>
                <TableCell sx={cellSx}>{ASSET_LABEL[s.asset_type] ?? s.asset_type}</TableCell>
                <TableCell sx={cellSx}>{s.coverage ? `${s.coverage.first_date} 〜 ${s.coverage.last_date}` : '未取込'}</TableCell>
                <TableCell sx={{ ...cellSx, textAlign: 'right' }}>{s.coverage?.rows_total?.toLocaleString() ?? 0}</TableCell>
                <TableCell sx={cellSx}>{s.coverage?.synced_at ? s.coverage.synced_at.slice(0, 16) : '-'}</TableCell>
                <TableCell sx={cellSx}>
                  <Switch size="small" checked={s.is_active} disabled={busyCode === s.symbol_code} onChange={(e) => handleToggle(s, e.target.checked)} />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Tooltip title="削除 (日足も削除)">
                    <span>
                      <IconButton size="small" disabled={busyCode === s.symbol_code} onClick={() => handleRemove(s)}>
                        <Icons.Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {symbols.length === 0 && (
              <TableRow><TableCell colSpan={9} sx={{ ...cellSx, textAlign: 'center', color: '#94a3b8' }}>銘柄が登録されていません</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
}
